/**
 * Reliable client-side printing for accounting vouchers and reports.
 *
 * Instead of fighting the app chrome and Bootstrap modal clipping with a
 * `@media print` overlay (which reserved layout space → blank pages, and the
 * scrollable modal clipped long vouchers to one page), we lift the already
 * rendered content into a standalone print document with its own print CSS and
 * page-break rules — the same proven pattern the contract history export uses.
 * This reuses the live DOM (no data duplication) and prints cleanly, hiding all
 * navigation, buttons and widgets.
 */

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Self-contained print stylesheet (no dependency on the app's CSS bundle). */
const PRINT_STYLES = `
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #212529; font-size: 12px; line-height: 1.45; margin: 0; }
  h1, h2, h3, h4, h5, h6 { margin: 0 0 8px; page-break-after: avoid; }
  h5 { font-size: 16px; }
  h6 { font-size: 13px; }
  hr { border: 0; border-top: 1px solid #dee2e6; margin: 12px 0; }
  dl { margin: 0; break-inside: avoid; }
  dt { font-size: 11px; color: #6c757d; margin-top: 6px; }
  dd { margin: 0 0 4px; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; page-break-inside: auto; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  th, td { border: 1px solid #dee2e6; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f1f3f5; font-size: 11px; }
  th.text-end, td.text-end { text-align: right; }
  .table-responsive { overflow: visible !important; }
  .font-monospace { font-family: 'Courier New', monospace; }
  .text-muted, .small { color: #6c757d; }
  .small { font-size: 11px; }
  .text-danger { color: #b02a37; }
  .text-success { color: #146c43; }
  .fw-semibold, .fw-bold { font-weight: 700; }
  .badge { border: 1px solid #adb5bd; padding: 1px 5px; border-radius: 3px; background: transparent !important; color: #212529 !important; font-weight: 400; font-size: 11px; }
  .border { border: 1px solid #dee2e6 !important; border-radius: 4px; }
  .rounded { border-radius: 4px; }
  .p-3 { padding: 12px; }
  .mb-3 { margin-bottom: 12px; }
  .row { display: flex; flex-wrap: wrap; gap: 12px; }
  .col-md-6 { flex: 1 1 45%; }
  .col-md-4 { flex: 1 1 30%; }
  .text-break { word-break: break-word; }
  /* Hide form controls, close buttons and decorative icons. Render inline
     buttons (e.g. voucher reference numbers) as plain text so their content
     survives in the printout. */
  .btn-close, input, select, textarea,
  [class^="feather-"], [class*=" feather-"], .d-print-none { display: none !important; }
  button { all: unset !important; font: inherit; color: inherit; cursor: default; }
  /* Bootstrap print utilities + keep app-hidden content hidden. */
  .d-none, .d-print-none { display: none !important; }
  .d-print-block { display: block !important; }
  .d-print-table { display: table !important; }
  /* Explicit page break between printed sections/vouchers. */
  .print-page-break { page-break-before: always; }
`;

const buildDocument = (title: string, bodyHtml: string): string =>
  `<!doctype html><html lang="sv"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
  `<style>${PRINT_STYLES}</style></head>` +
  `<body onload="window.focus();window.print();">` +
  `<h1 style="font-size:18px;margin:0 0 12px;">${escapeHtml(title)}</h1>${bodyHtml}</body></html>`;

const printViaIframe = (html: string): void => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html.replace('window.focus();window.print();', ''));
  doc.close();
  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  iframe.contentWindow?.addEventListener('afterprint', cleanup);
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    cleanup();
  }, 250);
};

/** Print an arbitrary HTML fragment in a clean standalone document. */
export const printHtmlFragment = (title: string, bodyHtml: string): void => {
  const html = buildDocument(title, bodyHtml);
  // NOTE: do NOT pass 'noopener' — window.open then returns null and nothing
  // opens. Print after the new document has loaded so it isn't blank.
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    printViaIframe(html);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
};

/**
 * Print the current rendered content of an element by id — reuses the live DOM
 * so labels, formatted amounts and dates are exactly what the user sees.
 */
export const printElementById = (elementId: string, title: string): void => {
  const element = document.getElementById(elementId);
  if (!element) return;
  printHtmlFragment(title, element.innerHTML);
};
